/**
 * GridSortHeader — 카드 그리드 상단의 표준 정렬 헤더.
 *
 * Workspaces 픽커(ProjectGrid)의 정렬 컨트롤(방향 토글 + 필드 Select, 우측
 * 카운트)을 기준으로 추출 (2026-08-12 L&F 통일). 방향 토글 테두리는 종전
 * 하드코딩(rgba 0,0,0,0.23) 대신 테마의 divider 를 쓴다 — 다크 테마 정합.
 */

import React from 'react';
import {
  Box,
  FormControl,
  IconButton,
  MenuItem,
  Select,
  Typography,
} from '@mui/material';
import {
  ArrowDownward as ArrowDownIcon,
  ArrowUpward as ArrowUpIcon,
} from '@mui/icons-material';

export type SortOrder = 'asc' | 'desc';

interface GridSortHeaderProps<Field extends string> {
  options: { value: Field; label: string }[];
  field: Field;
  onFieldChange: (field: Field) => void;
  order: SortOrder;
  onOrderToggle: () => void;
  /** 우측 카운트 문구 (예: "12개 프로젝트", "실행 중 1 · 완료 8") */
  countLabel: React.ReactNode;
}

function GridSortHeader<Field extends string>({
  options,
  field,
  onFieldChange,
  order,
  onOrderToggle,
  countLabel,
}: GridSortHeaderProps<Field>) {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        // 좁은 콘텐츠 영역에서 정렬 컨트롤·카운트가 한 줄을 넘치면 줄바꿈 (리뷰 반영)
        flexWrap: 'wrap',
        gap: 1,
        mb: 2,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        {/* 방향 토글 — 버튼 시맨틱(탭 순서·Enter/Space·접근 이름) 필수 (리뷰 반영) */}
        <IconButton
          onClick={onOrderToggle}
          aria-label={order === 'asc' ? '오름차순 (내림차순으로 전환)' : '내림차순 (오름차순으로 전환)'}
          title={order === 'asc' ? '오름차순' : '내림차순'}
          sx={{
            width: 40,
            height: 40,
            borderRadius: 1,
            border: '1px solid',
            borderColor: 'divider',
            transition: 'all 0.2s',
            '&:hover': {
              bgcolor: 'action.hover',
              borderColor: 'text.primary',
            },
          }}
        >
          {order === 'asc' ? (
            <ArrowUpIcon sx={{ fontSize: '1.25rem', color: 'action.active' }} />
          ) : (
            <ArrowDownIcon sx={{ fontSize: '1.25rem', color: 'action.active' }} />
          )}
        </IconButton>
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <Select
            value={field}
            onChange={(e) => onFieldChange(e.target.value as Field)}
            displayEmpty
            inputProps={{ 'aria-label': '정렬 기준' }}
            sx={{
              '& .MuiSelect-select': {
                display: 'flex',
                alignItems: 'center',
                py: '8.5px',
              },
            }}
          >
            {options.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ flexShrink: 0, ml: 2 }}>
        {countLabel}
      </Typography>
    </Box>
  );
}

export default GridSortHeader;
